import { serializeError } from 'serialize-error';

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../store';

export const APP_FEATURE_KEY = 'app';

export enum AppStatus {
  UNKNOWN = 'UNKNOWN',
  INIT = 'INIT', // app is initializing (load state)
  INIT_ERROR = 'INIT_ERROR', // app is having an error (error state - init variant)
  READY = 'READY', // app is ready
  ERROR = 'ERROR', // app is having an error (error state)
  BLOCKED = 'BLOCKED', // app is ready but is being blocked (block state = can not be clicked)
  SHUTDOWN = 'SHUTDOWN', // app is shuting down (load state)
}

export interface AppState {
  status: AppStatus;
  error: string | null;
  isProfileModalOpen: boolean;
  isProfileEditMode: boolean;
}

export const initialAppState: AppState = {
  status: AppStatus.INIT,
  error: null,
  isProfileModalOpen: false,
  isProfileEditMode: false
};

export const appSlice = createSlice({
  name: APP_FEATURE_KEY,
  initialState: initialAppState,
  reducers: {
    initApp(state) {
      state.status = AppStatus.INIT;
    },
    initAppSucceeded(state) {
      // if the startup encountered an error, do nothing
      if (state.status === AppStatus.ERROR) return;

      // otherwise we are ready to go
      state.status = AppStatus.READY;
    },
    // is mainly used to notify watchers that init failed and to trigger actions accordingly
    initAppFailed(state, action: PayloadAction<string>) {
      state.status = AppStatus.INIT_ERROR;
      state.error = action.payload;
    },
    appFailed(state, action: PayloadAction<string>) {
      state.status = AppStatus.ERROR;
      state.error = action.payload;
    },
    // is watched by the watcher and will trigger the msal logout, clear session & api reset if called
    logout(state) {
      state.status = AppStatus.SHUTDOWN;
    },
    setIsProfileModalOpen(state, action: PayloadAction<boolean>) {
      state.isProfileModalOpen = action.payload;
    },
    setIsProfileEditMode(state, action: PayloadAction<boolean>) {
      state.isProfileEditMode = action.payload;
    },
    blockApp(state) {
      // won't be able to block app while its not on ready state
      if (state.status !== AppStatus.READY) return;
      state.status = AppStatus.BLOCKED;
    },
    resumeApp(state) {
      // if current state is not on blocked then ignore
      if (state.status !== AppStatus.BLOCKED ) return;
      state.status = AppStatus.READY;
    }
  },
});

export const appReducer = appSlice.reducer;
export const appActions = appSlice.actions;

export const { initApp, initAppFailed, initAppSucceeded, appFailed, logout, setIsProfileModalOpen, setIsProfileEditMode, blockApp, resumeApp } = appActions;

export const getAppState = (rootState: RootState): AppState => rootState[APP_FEATURE_KEY];

export const createAppError = (error: unknown) => JSON.stringify(serializeError(error), null, 4);

export const selectAppStatus = createSelector(getAppState, (app) => app.status);
export const selectAppError = createSelector(getAppState, (app) => app.error);
